import { Controller, type UseFormReturn, useWatch } from 'react-hook-form'

import { AddressFields } from '@/components/AddressFields'
import { Field, FieldError, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FillFieldKey, useFieldCheck } from '@/models/fillApplication'
import {
  type BusinessDetailsValues,
  ELD_NONE,
  ELD_OTHER,
  ELD_PROVIDERS,
  ENTITY_TYPE_OPTIONS,
} from './useBusinessDetailsForm'

interface BusinessDetailsFieldsProps {
  form: UseFormReturn<BusinessDetailsValues>
  coverageLapseDetected?: boolean
  /** The Intake page sets the DOT on its FMCSA step and shows it above the form instead. */
  hideDotNumber?: boolean
  /** Shown but not editable: the customer's DOT comes from the FMCSA lookup, which an edit would skip. */
  lockDotNumber?: boolean
  /** Staff only; the customer's editor never asked for it. */
  showMcNumber?: boolean
  /** Two pairs the fields side by side on wide screens, for pages wider than a dialog. */
  columns?: 1 | 2
  idPrefix?: string
}

export function BusinessDetailsFields({
  form,
  coverageLapseDetected,
  hideDotNumber,
  lockDotNumber,
  showMcNumber,
  columns = 1,
  idPrefix = 'bd',
}: BusinessDetailsFieldsProps) {
  const { control, setValue, formState } = form
  const state = useWatch({ control, name: 'state' })
  const selectedEld = useWatch({ control, name: 'eldProvider' })
  const id = (name: string) => `${idPrefix}-${name}`
  const addressError = (['line1', 'line2', 'city', 'state', 'zip'] as const)
    .map((field) => formState.errors[field]?.message)
    .find((message) => !!message)
  // Shown at the field while it stands, as the field's own errors are.
  const companyNameCheck = useFieldCheck(FillFieldKey.COMPANY_NAME)
  const entityTypeCheck = useFieldCheck(FillFieldKey.ENTITY_TYPE)
  const addressCheck = useFieldCheck(FillFieldKey.ADDRESS)
  const caNumberCheck = useFieldCheck(FillFieldKey.CA_NUMBER)
  const cancellationReasonCheck = useFieldCheck(FillFieldKey.CANCELLATION_REASON)

  const companyName = (
    <Controller
      name="name"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.COMPANY_NAME}>
          <Label htmlFor={id('company-name')}>Company name</Label>
          <Input
            {...field}
            id={id('company-name')}
            aria-invalid={!!(fieldState.error || companyNameCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message ?? companyNameCheck}</FieldError>
        </Field>
      )}
    />
  )
  const entityType = (
    <Controller
      name="entityType"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.ENTITY_TYPE}>
          <Label htmlFor={id('entity-type')}>Business type</Label>
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger
              id={id('entity-type')}
              className="w-full"
              aria-invalid={!!(fieldState.error || entityTypeCheck) || undefined}
            >
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{fieldState.error?.message ?? entityTypeCheck}</FieldError>
        </Field>
      )}
    />
  )
  const eldProvider = (
    <Controller
      name="eldProvider"
      control={control}
      render={({ field }) => (
        <Field>
          <Label htmlFor={id('eld-provider')}>ELD provider</Label>
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger id={id('eld-provider')} className="w-full">
              <SelectValue placeholder="Select ELD provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ELD_NONE}>No ELD</SelectItem>
              {ELD_PROVIDERS.map((provider) => (
                <SelectItem key={provider.value} value={provider.value}>
                  {provider.label}
                </SelectItem>
              ))}
              <SelectItem value={ELD_OTHER}>Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  )
  const eldProviderOther = selectedEld === ELD_OTHER && (
    <Controller
      name="eldProviderOther"
      control={control}
      render={({ field }) => (
        <Field>
          <Label htmlFor={id('eld-provider-other')}>ELD provider name</Label>
          <Input {...field} id={id('eld-provider-other')} placeholder="e.g. Omnitracs" />
        </Field>
      )}
    />
  )
  const dotNumber = !hideDotNumber && (
    <Controller
      name="dotNumber"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.DOT_NUMBER}>
          <Label htmlFor={id('dot-number')}>DOT number</Label>
          <Input
            {...field}
            id={id('dot-number')}
            disabled={lockDotNumber}
            aria-invalid={!!fieldState.error || undefined}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  )
  const mcNumber = showMcNumber && (
    <Controller
      name="mcNumber"
      control={control}
      render={({ field, fieldState }) => (
        <Field>
          <Label htmlFor={id('mc-number')}>MC number</Label>
          <Input {...field} id={id('mc-number')} aria-invalid={!!fieldState.error || undefined} />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  )
  const address = (
    <div data-fill-field={FillFieldKey.ADDRESS} className="flex flex-col gap-2">
      <AddressFields
        control={control}
        setValue={setValue}
        names={{ line1: 'line1', line2: 'line2', city: 'city', state: 'state', zip: 'zip' }}
      />
      <FieldError>{addressError ?? addressCheck}</FieldError>
    </div>
  )
  const yearsInBusiness = (
    <Controller
      name="yearsInBusiness"
      control={control}
      render={({ field, fieldState }) => (
        <Field>
          <Label htmlFor={id('years')}>Years in business</Label>
          <Input {...field} id={id('years')} type="number" min={0} />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  )
  const caNumber = state === 'CA' && (
    <Controller
      name="caNumber"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.CA_NUMBER}>
          <Label htmlFor={id('ca-number')}>CA number</Label>
          <Input
            {...field}
            id={id('ca-number')}
            placeholder="CA1234567"
            aria-invalid={!!(fieldState.error || caNumberCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message ?? caNumberCheck}</FieldError>
        </Field>
      )}
    />
  )
  const cancellationReason = coverageLapseDetected && (
    <Controller
      name="cancellationReason"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.CANCELLATION_REASON}>
          <Label htmlFor={id('cancellation-reason')}>Reason for coverage lapse</Label>
          <Textarea
            {...field}
            id={id('cancellation-reason')}
            aria-invalid={!!(fieldState.error || cancellationReasonCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message ?? cancellationReasonCheck}</FieldError>
        </Field>
      )}
    />
  )

  if (columns === 2) {
    return (
      // The address runs full width between two paired blocks: its street line is the longest value here.
      <FieldGroup>
        <div className="grid gap-x-4 gap-y-7 md:grid-cols-2">
          <div className="flex flex-col gap-7">
            {companyName}
            {entityType}
          </div>
          <div className="flex flex-col gap-7">
            {dotNumber}
            {mcNumber}
          </div>
        </div>
        {address}
        <div className="grid gap-x-4 gap-y-7 md:grid-cols-2">
          <div className="flex flex-col gap-7">
            {eldProvider}
            {eldProviderOther}
            {caNumber}
          </div>
          <div className="flex flex-col gap-7">{yearsInBusiness}</div>
        </div>
        {cancellationReason}
      </FieldGroup>
    )
  }

  return (
    <FieldGroup>
      {companyName}
      {entityType}
      {eldProvider}
      {eldProviderOther}
      {dotNumber}
      {mcNumber}
      {address}
      {yearsInBusiness}
      {caNumber}
      {cancellationReason}
    </FieldGroup>
  )
}
